// src/context/AuthProvider.tsx
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import api from "../lib/api";
import { accountIdentity } from "../lib/accountIdentity";
import { cancelSessionRenewal, clearToken, getToken, hasPersistentSession, isLoginRemembered, LS_TOKEN, markPersistentSession, setToken, type PersistMode } from "../lib/auth";

export type User = {
  id: number | string;
  name: string;
  email: string;
  is_admin: boolean;
  is_merchant?: boolean;
  verification_exempt?: boolean;
  is_social?: boolean;
  social_provider?: string | null;
  social_id?: string | null;
  ekyc_status?: "verified" | "pending" | "unverified" | null;
  account_status?: "verified" | "pending" | "unverified" | null;
};

type SignupInput =
  | { name: string; email: string; password: string }
  | { name: string; email: string; password: string; idcard: File };

type Ctx = {
  user: User | null;
  ready: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<User>;
  logout: () => Promise<void>;
  signup: (p: SignupInput) => Promise<void>;
  refresh: () => Promise<User | null>;
};

const AuthCtx = createContext<Ctx | null>(null);

export const useAuth = () => {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
};

// axios: 쿠키 동반
api.defaults.withCredentials = true;

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const refreshRequest = useRef(0);
  const authOperation = useRef(0);

  // Authorization 헤더 관리
  const setAuthHeader = useCallback((token: string | null, mode: PersistMode = "local") => {
    refreshRequest.current += 1;
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      setToken(token, mode);
    } else {
      delete api.defaults.headers.common.Authorization;
      clearToken();
    }
  }, []);

  const refresh = useCallback(async (): Promise<User | null> => {
    const request = ++refreshRequest.current;
    const operation = authOperation.current;
    const isCurrent = () => request === refreshRequest.current && operation === authOperation.current;
    try {
      const { data } = await api.get("/me");
      const u: User | null = data?.user ?? data ?? null;
      if (!isCurrent()) return null;
      if (!u || !accountIdentity(u.id)) throw new Error("사용자 정보를 확인하지 못했어요.");
      if ((u.is_admin === true || isLoginRemembered()) && !hasPersistentSession()) {
        // Preserve valid legacy logins while upgrading them to renewable sessions.
        try {
          const enabled = await api.post("/auth/session/enable", { remember: true });
          if (!isCurrent()) return null;
          if (enabled.data?.persistent === true && enabled.data?.token) {
            markPersistentSession();
            setToken(enabled.data.token, "local");
          }
        } catch { /* A temporary outage must not discard a valid login. */ }
      }
      setUser(u);
      return u;
    } catch {
      if (isCurrent()) setUser(null);
      return null;
    } finally {
      if (isCurrent()) setReady(true);
    }
  }, []);

  useEffect(() => {
    // 초기화: 저장된 토큰을 헤더에 연결하고 me 조회
    const token = getToken();
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
    refresh();

    // 탭 간 토큰 동기화: localStorage 토큰 기준
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_TOKEN) {
        authOperation.current += 1;
        refreshRequest.current += 1;
        const t = getToken();
        if (t) {
          api.defaults.headers.common.Authorization = `Bearer ${t}`;
        } else {
          delete api.defaults.headers.common.Authorization;
        }
        setUser(null);
        setReady(false);
        void refresh();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => {
      refreshRequest.current += 1;
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  const login = useCallback(async (
    email: string,
    password: string,
    remember: boolean = true
  ): Promise<User> => {
    const operation = ++authOperation.current;
    cancelSessionRenewal();
    try {
      const { data } = await api.post("/login", { email, password, remember });
      if (operation !== authOperation.current) throw new Error("로그인 요청이 변경되었어요. 다시 시도해 주세요.");
      const token: string | undefined = data?.token;
      const mode: PersistMode = data?.persistent === true || remember ? "local" : "session";
      if (data?.persistent === true) markPersistentSession();
      setAuthHeader(token ?? null, mode);
      const me = await refresh();
      if (!me || operation !== authOperation.current) throw new Error("사용자 정보를 확인하지 못했어요. 다시 로그인해 주세요.");
      return me;
    } catch (error) {
      if (operation === authOperation.current) {
        setAuthHeader(null);
        setUser(null);
        setReady(true);
      }
      throw error;
    }
  }, [refresh, setAuthHeader]);

  const signup = useCallback(async (p: SignupInput) => {
    if ("idcard" in p && p.idcard instanceof File) {
      const fd = new FormData();
      fd.append("name", p.name);
      fd.append("email", p.email);
      fd.append("password", p.password);
      fd.append("idcard", p.idcard);
      await api.post("/signup", fd);
    } else {
      await api.post("/signup", p as { name: string; email: string; password: string });
    }
  }, []);

  const logout = useCallback(async () => {
    const operation = ++authOperation.current;
    cancelSessionRenewal();
    refreshRequest.current += 1;
    try {
      await api.post("/logout").catch(() => {});
    } finally {
      if (operation === authOperation.current) {
        setAuthHeader(null);
        setUser(null);
        setReady(true);
      }
    }
  }, [setAuthHeader]);

  const value = useMemo(
    () => ({ user, ready, login, logout, signup, refresh }),
    [user, ready, login, logout, signup, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
